<template>
    <div class="dashboard" :style="themeVars">
        <loader v-if="loading" />

        <place-header />

        <template v-if="hasActiveProgram">
        <tab-bar class="dashboard-tabs" v-if="isLogged" />

        <section class="dashboard-summary">
            <h1 class="dashboard-greeting">{{ greeting }}</h1>

            <points-ring :value="balance" :progress="progress" :color="ringColor" />

            <div class="dashboard-tiers" v-if="memberTiers.length">
                <tier-chip v-for="tier in memberTiers" :key="tier.id" :tier="tier" @click="openTiers(tier.id)" />
            </div>

            <button
                v-if="!isLogged"
                type="button"
                class="dashboard-login"
                @click="$router.push('/')"
            >
                <span v-lang="'dashboard-loginButton'"></span>
            </button>
        </section>
        </template>

        <section class="dashboard-content">
            <banner-carousel v-if="hasActiveProgram" :banners="banners" />

            <template v-if="isLogged && coupons.length">
                <div class="dashboard-section-header">
                    <h2 class="dashboard-section-title" v-lang="'dashboard-activeCoupons'"></h2>
                </div>

                <div class="dashboard-coupons">
                    <coupon-card v-for="coupon in coupons" :key="coupon.id" :coupon="coupon" />
                </div>
            </template>

            <div v-if="!hasActiveProgram && !loading" class="dashboard-no-program">
                <h1 v-lang="'common-noProgram-title'"></h1>
                <p v-lang="'common-noProgram-description'"></p>
            </div>

            <template v-if="hasActiveProgram">
            <div class="dashboard-section-header">
                <h2 class="dashboard-section-title" v-lang="'dashboard-rewardsTitle'"></h2>
                <p class="dashboard-section-subtitle" v-lang="'dashboard-rewardsSubtitle'"></p>
            </div>

            <p class="dashboard-empty" v-if="!rewards.length" v-lang="'dashboard-rewardsEmpty'"></p>

            <ol class="dashboard-timeline" v-else>
                <li class="dashboard-step" v-for="(reward, index) in rewards" :key="reward.id">
                    <reward-card
                        :reward="reward"
                        :balance="balance"
                        :is-logged="isLogged"
                        :tiers="tiers"
                        selectable
                        @select="openReward"
                    />

                    <div
                        class="dashboard-connector"
                        :class="{ 'is-reached': isConnectorReached(index) }"
                        v-if="index < rewards.length - 1"
                    >
                        <span class="dashboard-pill" v-if="missingLabel(index)">
                            {{ missingLabel(index) }}
                        </span>
                    </div>
                </li>
            </ol>
            </template>
        </section>

        <footer class="dashboard-footer">
            <img src="/images/logo-kevi-white.svg" alt="Kevi" class="dashboard-kevi" @error="hideImage" />
        </footer>
    </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from "vuex";

require("../points/points.store");
import loader from "../../shared/loader";
import PlaceHeader from "../../shared/place-header";
import TabBar from "../../shared/tab-bar";
import RewardCard from "../points/points-reward-card";
import PointsRing from "../points/points-ring";
import BannerCarousel from "../points/points-banners";
import CouponCard from "../../shared/coupon-card";
import TierChip from "../../shared/tier-chip";
import { withHash } from "../../helpers/color";

export default {
    components: {
        loader,
        PointsRing,
        BannerCarousel,
        RewardCard,
        CouponCard,
        PlaceHeader,
        TabBar,
        TierChip,
    },
    mounted() {
        if (this.isLogged) {
            this.$store.dispatch("updateClientInfo").then(() => {
                if (!this.clientHasProfileCompleted) {
                    this.$router.push("/profile");
                }
            });
        }

        if (this.place.id) {
            this.$store.dispatch("dashboard/loadData");
        }
    },
    computed: {
        ...mapState({
            place: (state) => state.place,
            client: (state) => state.client,
            token: (state) => state.token,
            balance: (state) => state.dashboard.balance,
            rewards: (state) => state.dashboard.rewards,
            tiers: (state) => state.dashboard.tiers,
            coupons: (state) => state.dashboard.coupons,
            banners: (state) => state.dashboard.banners,
            loading: (state) => state.dashboard.loading,
        }),
        ...mapGetters(["clientHasProfileCompleted", "hasActiveProgram"]),
        ...mapGetters("dashboard", ["progress"]),
        isLogged() {
            return !!(this.client && this.client.id && this.token);
        },
        benefitConfig() {
            return this.place.benefit_program || {};
        },
        primaryColor() {
            return withHash(
                this.benefitConfig.primaryColor || this.place.mainColor || this.place.color || "#2aaa4c"
            );
        },
        memberTiers() {
            return this.tiers.filter((tier) => tier.isMember).sort((a, b) => a.position - b.position);
        },
        ringColor() {
            const highest = this.memberTiers[this.memberTiers.length - 1];

            return (highest && withHash(highest.color)) || this.primaryColor;
        },
        greeting() {
            return this.isLogged && this.client.first_name
                ? this.t("dashboard-hello", { name: this.client.first_name })
                : this.t("dashboard-helloVisitor");
        },
        themeVars() {
            return {
                "--place-color": this.primaryColor,
            };
        },
    },
    watch: {
        "place.id"() {
            this.$store.dispatch("dashboard/loadData");
        },
    },
    methods: {
        ...mapMutations(["openModal", "openTiers"]),
        openReward(reward) {
            this.$store.commit("openReward", reward.id);
        },
        missingPoints(index) {
            const next = this.rewards[index + 1];

            return next && !["locked", "blurred"].includes(next.access) ? next.points - this.balance : 0;
        },
        isConnectorReached(index) {
            const visible = (reward) => reward.access !== "blurred";
            const target =
                this.rewards.slice(index + 1).find(visible) ||
                this.rewards.slice(0, index + 1).reverse().find(visible);

            return this.isLogged && !!target && this.balance >= target.points;
        },
        missingLabel(index) {
            const missing = this.missingPoints(index);

            if (missing <= 0) {
                return "";
            }

            return missing === 1
                ? this.t("dashboard-missingOnePoint")
                : this.t("dashboard-missingPoints", { points: missing });
        },
        hideImage(event) {
            event.target.style.display = "none";
        },
    },
};
</script>

<style lang="scss">
.dashboard {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
}

.dashboard-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 1rem;
}

.dashboard-greeting {
  margin: 0;
  font-size: clamp(0.9375rem, 4.3vw, 1rem);
  font-weight: 500;
  line-height: 1.3;
  color: #1f2a37;
  text-align: center;
}

.dashboard-tiers {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  max-width: 20rem;
}

.dashboard-login {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: #2aaa4c;
  color: #f1fcf3;
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.98);
  }
}

.dashboard-tabs {
  width: 100%;
  margin-top: 0.75rem;
}

.dashboard-coupons {
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dashboard-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1rem 3rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 1.5rem 1.5rem 0 0;
}

.dashboard-section-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.dashboard-section-title {
  margin: 0;
  font-size: clamp(1rem, 4.8vw, 1.125rem);
  font-weight: 400;
  line-height: 1.3;
  color: #4d5761;
}

.dashboard-section-subtitle {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.2;
  color: #4d5761;
}

.dashboard-empty {
  margin: 0;
  padding: 1.5rem 0;
  font-size: 0.875rem;
  color: #6c737f;
  text-align: center;
}

.dashboard-no-program {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;

  h1 {
    color: #384250;
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
  }

  p {
    color: #6c737f;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
    max-width: 22rem;
  }
}

.dashboard-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-step {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-connector {
  position: relative;
  width: 0.125rem;
  height: 3rem;
  background: #d2d6db;
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-reached {
    background: #17b26a;
  }
}

.dashboard-pill {
  position: absolute;
  padding: 0.25rem;
  border-radius: 0.25rem;
  background: #d2d6db;
  color: #384250;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
}

.dashboard-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.75rem;
  flex: 0 0 auto;
  background: #0d121c;

  @media (min-width: 64rem) {
    height: 4.5rem;
  }
}

.dashboard-kevi {
  height: 1.5625rem;
  display: block;

  @media (min-width: 64rem) {
    height: 2.5rem;
  }
}
</style>
